import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { guardRoute } from "@/lib/policy/route-guard";
import { endSession } from "@/lib/session/sign-out";
import { getKioskDevice } from "./-components/kiosk-api";
import { KioskStaffSetup } from "./-components/kiosk-staff-setup";

const IDLE_MS = 3 * 60_000;

export const Route = createFileRoute("/kiosk/switch")({
  beforeLoad: ({ context }) => guardRoute(context.me, "visitor-kiosk"),
  component: KioskSwitch,
});

/** A switch left half done in the lobby: after three untouched minutes the staff session goes and
 * the kiosk comes back, so the next visitor never finds a signed-in administrator. */
function useBackToKioskWhenIdle() {
  useEffect(() => {
    let timer = setTimeout(leave, IDLE_MS);
    const touched = () => {
      clearTimeout(timer);
      timer = setTimeout(leave, IDLE_MS);
    };
    window.addEventListener("pointerdown", touched);
    window.addEventListener("keydown", touched);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("pointerdown", touched);
      window.removeEventListener("keydown", touched);
    };
  }, []);
}

function leave() {
  void endSession(null, "/kiosk");
}

/** Where five taps on the welcome lead after a fresh sign-in: the same setup as a new tablet, from
 * the building it serves now. Installing replaces and revokes the old registration; Keep signs out
 * and puts the kiosk back as it was. */
function KioskSwitch() {
  const { me } = Route.useRouteContext();
  const [device] = useState(getKioskDevice);
  useBackToKioskWhenIdle();
  if (!me) {
    return (
      <main className="fixed inset-0 z-40 flex flex-col items-center justify-center gap-6 p-6">
        <h1 className="text-2xl font-semibold">Sign in to switch this tablet</h1>
        <Button asChild className="h-12 px-8 text-[15px]">
          <Link to="/login" search={{ next: "/kiosk/switch" }}>
            Sign in
          </Link>
        </Button>
        <Button asChild variant="ghost" className="h-12 text-[15px]">
          <Link to="/kiosk">Back to the kiosk</Link>
        </Button>
      </main>
    );
  }
  return <KioskStaffSetup current={device ? { facilityName: device.facilityName } : undefined} />;
}
